import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  fetchChartNaverOfferCount,
  fetchDailyAverageDanjiPrice,
  fetchDailyRealTradePrice,
  fetchMonthlyAverageDanjiPrice,
  fetchRealTradePrice,
} from 'api/chart';
import { fetchComplexPyeongList } from 'api/complex';
import EmptyData from 'components/EmptyData';
import WdbModal from 'components/WdbModal';
import ChartDateToggleButtonGroup from 'components/buttonGroup/ChartDateToggleButtonGroup';
import ChartToggleButtonGroup from 'components/buttonGroup/ChartToggleButtonGroup';
import NaverOfferCountChart from 'components/chart/NaverOfferCountChart';
import QuoteChart from 'components/chart/QuoteChart';
import ArrowDownIcon from 'components/icon/ArrowDownIcon';
import CloseIcon from 'components/icon/CloseIcon';
import InfoIcon from 'components/icon/InfoIcon';
import ChartSelect from 'components/select/ChartSelect';
import ButtonTooltip from 'components/tooltip/ButtonTooltip';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setAppVersion } from 'redux/store/AppReducer';
import { RootState } from 'redux/store/rootReducer';
import {
  QUOTE_CHART_DATE_BUTTONS_FILTER,
  QUOTE_CHART_DATE_SELECT_FILTER,
} from 'static/chart';
import styled from 'styled-components';
import {
  ChartOfferFormResponse,
  ChartOption,
  DateFilter,
  PriceChartParams,
  QuoteChartData,
  QuoteChartFilter,
  TradeType,
} from 'types/chart';
import { TRADE_TYPE } from 'types/offer';
import { Message, sendReactNativeMessage } from 'utils/ReactNativeMessage';
import {
  filterZeroAndGetMax,
  filterZeroAndGetMin,
  getDateRangeForFilter,
  getIsVisibleDay,
} from 'utils/chart';
import {
  chartYtickFormatter,
  formatPriceToKorean,
  generateDayArray,
  generateMonthArray,
} from 'utils/formatUtil';
import OwnerCertificationChartPage from './OwnerCertificationChartPage';

/* eslint-disable @typescript-eslint/no-var-requires */
const semver = require('semver');

const INIT_START_DATE = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
const INIT_END_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

function QuoteChartPage() {
  const dispatch = useDispatch();
  const { appVersion } = useSelector((state: RootState) => state.appStore);
  // 네이버매물차트 추가(그에 따른 높이 변경), 일자/평 필터 방식 변경에 대한 버전(갈아타기 차트 내용 포함)
  const versionWithAddNavrOfferChartChangeDatePyeongFilter = '2.52.0';
  const isLatestFilterAppVersion = semver.gte(
    appVersion,
    versionWithAddNavrOfferChartChangeDatePyeongFilter,
  );

  const location = useLocation();
  const { search } = location;
  const parameter = new URLSearchParams(search);
  const urlComplexId = parameter.get('complexId');
  const urlAreaInPyeong = parameter.get('areaInPyeong');
  const urlTradeType = parameter.get('tradeType');

  const [chartLoading, setChartLoading] = useState<boolean>(true);

  const [chartRealTradeLineData, setChartRealTradeLineData] = useState<
    QuoteChartData[]
  >([]);
  const [chartScatterData, setChartScatterData] = useState<QuoteChartData[]>(
    [],
  );
  const [naverOfferCountChartData, setNaverOfferCountChartData] = useState<
    ChartOfferFormResponse[]
  >([]);

  const [params, setParams] = useState<PriceChartParams>({
    areaInPyeong: undefined,
    complexId: undefined,
    startDate: INIT_START_DATE,
    endDate: INIT_END_DATE,
    tradeType: 'TRADE',
  });
  const { complexId, tradeType, startDate, endDate, areaInPyeong } = params;

  const [filter, setFilter] = useState<QuoteChartFilter>({
    dateFilter: 'THREE_YEAR',
    pyeongList: [],
    isVisibleRealTradeTypeChart: true,
    guideTooltipOpen: false,
  });
  const {
    dateFilter,
    pyeongList,
    isVisibleRealTradeTypeChart,
    guideTooltipOpen,
  } = filter;

  const [quoteChartOption, setQuoteChartOption] = useState<ChartOption>({
    tooltipActive: false,
  });
  const [naverOfferCountChartOption, setNaverOfferCountChartOption] =
    useState<ChartOption>({
      tooltipActive: false,
      yAxisProps: {
        hide: true,
      },
    });
  const [totalNaverOfferCount, setTotalNaverOfferCount] = useState(0);

  const [isEmpty, setIsEmpty] = useState({
    isChartEmpty: false,
    isRealTradePriceByDateEmpty: false,
  });
  const { isChartEmpty, isRealTradePriceByDateEmpty } = isEmpty;

  // startDate와 endDate를 dayjs 객체로 변환
  const [isVisibleDay, setIsVisibleDay] = useState(false);
  const [chartKey, setChartKey] = useState('');
  const [latestDanjiPrice, setLatestDanjiPrice] = useState('');
  const [ownerCertificationModalOpen, setOwnerCertificationModalOpen] =
    useState<boolean>(false);

  const handleTradeType = (tradeType: TradeType) => {
    setParams((prev) => ({
      ...prev,
      tradeType,
    }));
    postMessage('tradeType', tradeType);
  };

  const postMessage = (type, payload) => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type, payload }));
  };

  const handleDateFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    filter: DateFilter,
  ) => {
    const date = getDateRangeForFilter(filter);
    const isVisible = getIsVisibleDay(date);

    setParams((prev) => ({
      ...prev,
      ...date,
    }));
    setIsVisibleDay(isVisible);
    setFilter((prev) => ({
      ...prev,
      dateFilter: filter,
    }));
  };

  const handleDateFilter = (event: SelectChangeEvent<unknown>) => {
    const filter = event.target.value as DateFilter;
    const date = getDateRangeForFilter(filter);
    const isVisible = getIsVisibleDay(date);

    setParams((prev) => ({
      ...prev,
      ...date,
    }));
    setIsVisibleDay(isVisible);
    setFilter((prev) => ({
      ...prev,
      dateFilter: filter,
    }));
  };

  const handlePyeongFilter = (event: SelectChangeEvent<unknown>) => {
    setParams((prev) => ({
      ...prev,
      areaInPyeong: Number(event.target.value as string),
    }));
    postMessage('areaInPyeong', Number(event.target.value as string));
  };

  const handleClickPyeongFilter = () => {
    const message: Message = {
      type: 'openAreaDrawer',
    };
    sendReactNativeMessage(message);
  };

  const handleClickNaverOfferChart = () => {
    openOwnerCertificationModal();
  };

  const openOwnerCertificationModal = () => {
    setOwnerCertificationModalOpen(true);
  };

  const closeOwnerCertificationModal = () => {
    setOwnerCertificationModalOpen(false);
  };

  const toggleVisibleRealTradeTypeChart = () => {
    setFilter((prev) => ({
      ...prev,
      isVisibleRealTradeTypeChart: !isVisibleRealTradeTypeChart,
    }));
  };

  const openGuideTooltip = () => {
    setFilter((prev) => ({
      ...prev,
      guideTooltipOpen: true,
    }));
  };
  const closeGuideTooltip = () => {
    setFilter((prev) => ({
      ...prev,
      guideTooltipOpen: false,
    }));
  };

  const showQuoteChartTooltip = () => {
    setQuoteChartOption((prev) => ({
      ...prev,
      tooltipActive: true,
    }));
  };

  const hideQuoteChartTooltip = () => {
    setQuoteChartOption((prev) => ({
      ...prev,
      tooltipActive: false,
    }));
  };

  const fetchData = async (resultParams: PriceChartParams) => {
    setChartLoading(true);

    const responseDailyDanjiAveragePrice = await fetchDailyAverageDanjiPrice(
      resultParams,
    );

    const responseMonthlyDanjiAveragePrice =
      await fetchMonthlyAverageDanjiPrice(resultParams);

    const responseRealTradePrice = await fetchRealTradePrice(resultParams);
    // 해제 거래(cancelDealDate 존재)는 차트 점/계산에서 모두 제외
    const realTradePriceList =
      responseRealTradePrice.data?.data.filter(
        (value) => !value.cancelDealDate,
      ) || [];

    const responseDailyRealTradePrice = await fetchDailyRealTradePrice(
      resultParams,
    );

    const responseNaverOfferCount = await fetchChartNaverOfferCount(
      resultParams,
    );

    const isChartEmpty = isVisibleDay
      ? responseDailyDanjiAveragePrice.data?.data.every(
          (value) => value.averagePrice === null,
        ) || false
      : responseMonthlyDanjiAveragePrice.data?.data.every(
          (value) => value.averagePrice === null,
        ) || false;

    setIsEmpty((prev) => ({
      ...prev,
      isChartEmpty,
      isRealTradePriceByDateEmpty:
        responseDailyRealTradePrice.data?.data.length === 0,
    }));

    if (responseDailyRealTradePrice.data?.data.length === 0) {
      setFilter((prev) => ({
        ...prev,
        isVisibleRealTradeTypeChart: false,
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        isVisibleRealTradeTypeChart: true,
      }));
    }

    setNaverOfferCountChartData(responseNaverOfferCount.data?.data || []);

    const totalNaverOfferCountSum =
      responseNaverOfferCount.data?.data.reduce(
        (acc, cur) => acc + cur.offerCount,
        0,
      ) || 0;
    setTotalNaverOfferCount(totalNaverOfferCountSum);

    const dayArray = generateDayArray(startDate, endDate);
    const monthArray = generateMonthArray(startDate, endDate);

    const getDateArray = () => {
      switch (dateFilter) {
        case 'ONE_MONTH':
          return dayArray;
        case 'THREE_MONTH':
          return dayArray;
        case 'ONE_YEAR':
          return monthArray;
        case 'THREE_YEAR':
          return monthArray;
        case 'NONE':
        default:
          return monthArray;
      }
    };
    const dateArray = getDateArray();

    // 단지표준가격 데이터
    const filterDailyDanjiAveragePrice = (date: string) =>
      responseDailyDanjiAveragePrice.data?.data
        .filter((value) => {
          const dateFormatted = value.createDate.replaceAll('-', '');

          return dateFormatted === date;
        })
        .map((value) => ({
          realTradePriceCount: value.realTradePriceCount,
          averagePrice: value.averagePrice,
        })) || [];

    const filterMonthlyDanjiAveragePrice = (date: string) => {
      return (
        responseMonthlyDanjiAveragePrice.data?.data.filter((value) => {
          const dateFormatted = value.yearMonth.replaceAll('-', '');

          return dateFormatted === date;
        }) || []
      );
    };

    // ************* 실거래가 차트 데이터 *************
    const filterRealTradePriceData = (yearMonthDayParam: string) => {
      return (
        realTradePriceList
          .filter((value) => {
            const yearMonthDay = value.contractDate.replaceAll('-', '');
            const yearMonth = value.contractDate
              .replaceAll('-', '')
              .slice(0, -2);
            const dateFormatted = isVisibleDay ? yearMonthDay : yearMonth;

            return dateFormatted === yearMonthDayParam;
          })
          .map((value) => ({
            price: value.price,
            tradeType: value.tradeType,
          })) || []
      );
    };
    // 차트에 찍히는 점(해제 거래 제외)과 동일한 소스로 건수/평균가 계산
    const getRealTradeSummary = (yearMonthDayParam: string) => {
      const trades = filterRealTradePriceData(yearMonthDayParam);

      if (trades.length === 0) return { price: undefined, count: 0 };

      const total = trades.reduce((acc, trade) => acc + trade.price, 0);

      return {
        price: Math.round(total / trades.length),
        count: trades.length,
      };
    };

    const woodeabbangAveragePriceByDateData =
      responseDailyDanjiAveragePrice.data?.data || [];
    const woodaebbangAveragePriceFilteredPrices =
      woodeabbangAveragePriceByDateData
        .map((value) => value.averagePrice)
        .filter((price): price is number => price !== null); // null인 요소를 제거

    const realPriceByDateData = realTradePriceList;
    const realPriceFilteredPrices = realPriceByDateData
      .map((value) => value.price)
      .filter((price): price is number => price !== null); // null인 요소를 제거
    const naverOfferCountPrices = responseNaverOfferCount.data?.data || [];
    const naverOfferCountPriceFilteredPrices = naverOfferCountPrices
      .map((value) => value.price)
      .filter((price): price is number => price !== null); // null인 요소를 제거

    const minWoodaebbangAveragePriceByDate =
      woodaebbangAveragePriceFilteredPrices.length
        ? filterZeroAndGetMin(woodaebbangAveragePriceFilteredPrices)
        : 0;
    const maxWoodaebbangAveragePriceByDate =
      woodaebbangAveragePriceFilteredPrices.length
        ? filterZeroAndGetMax(woodaebbangAveragePriceFilteredPrices)
        : 0;

    const minRealPriceByDate = realPriceFilteredPrices.length
      ? filterZeroAndGetMin(realPriceFilteredPrices)
      : 0;
    const maxRealPriceByDate = realPriceFilteredPrices.length
      ? filterZeroAndGetMax(realPriceFilteredPrices)
      : 0;

    const minNaverOfferCountPrice = naverOfferCountPriceFilteredPrices.length
      ? filterZeroAndGetMin(naverOfferCountPriceFilteredPrices)
      : 0;
    const maxNaverOfferCountPrice = naverOfferCountPriceFilteredPrices.length
      ? filterZeroAndGetMax(naverOfferCountPriceFilteredPrices)
      : 0;
    const min = filterZeroAndGetMin([
      minWoodaebbangAveragePriceByDate,
      minRealPriceByDate,
      minNaverOfferCountPrice,
    ]);

    const max = filterZeroAndGetMax([
      maxWoodaebbangAveragePriceByDate,
      maxRealPriceByDate,
      maxNaverOfferCountPrice,
    ]);

    const tickCount = 5;
    const { ticks, domain } = chartYtickFormatter({
      min,
      max,
      tickCount,
    });

    setQuoteChartOption((prev) => ({
      ...prev,
      yAxisProps: {
        tickCount,
        domain,
        ticks,
      },
    }));
    setNaverOfferCountChartOption((prev) => ({
      ...prev,
      yAxisProps: {
        ...prev.yAxisProps,
        tickCount,
        domain,
        ticks,
      },
    }));

    // ********** 차트 데이터 **************
    const chartData: QuoteChartData[] = dateArray.map((value) => {
      const dailyDanjiAveragePriceData = filterDailyDanjiAveragePrice(value);
      const monthlyDanjiAveragePriceData =
        filterMonthlyDanjiAveragePrice(value);

      const { price, count } = getRealTradeSummary(value);

      const woodaebbangAveragePrice = isVisibleDay
        ? Number(
            dailyDanjiAveragePriceData
              .map((value) => value.averagePrice)
              .join(''),
          )
        : Number(
            monthlyDanjiAveragePriceData
              .map((value) => value.averagePrice)
              .join(''),
          );

      if (woodaebbangAveragePrice === 0)
        return {
          yearMonth: value,
          date: dayjs(value).valueOf(),
          price,
          count, // 기본값 설정
        };

      return {
        yearMonth: value,
        date: dayjs(value).valueOf(),
        price,
        count, // 기본값 설정
        woodaebbangPrice: woodaebbangAveragePrice,
      };
    });

    // ************** Scatter Chart *************************
    const scatterData: QuoteChartData[] = dateArray.flatMap((value) => {
      const yearMonth = value.slice(0, 8);

      const tradeData = filterRealTradePriceData(value); // 해당 날짜의 거래 데이터 배열 가져오기
      const averageWoodaebbangData =
        filterDailyDanjiAveragePrice(yearMonth) || [];

      const { price, count } = getRealTradeSummary(value);

      const woodaebbangAveragePrice = Number(
        averageWoodaebbangData
          .map((woodaeMonthly) => woodaeMonthly.averagePrice)
          .join(''),
      );

      // 거래 데이터가 있는 경우 각각의 거래에 대해 객체를 생성하여 반환
      if (tradeData.length > 0) {
        return tradeData.map((trade) => {
          const realPrice = trade.price;

          if (woodaebbangAveragePrice === 0) {
            return {
              yearMonth,
              date: dayjs(value).valueOf(),
              realPrice,
              price,
              count, // 기본값 설정
            };
          }

          return {
            yearMonth,
            date: dayjs(value).valueOf(),
            realPrice,
            price,
            count, // 기본값 설정
            woodaebbangPrice: woodaebbangAveragePrice,
          };
        });
      }
      // 거래 데이터가 없는 경우 해당 날짜에 빈 문자열을 가진 객체 하나를 반환
      if (woodaebbangAveragePrice === 0) {
        return [
          {
            date: dayjs(value).valueOf(),
            yearMonth,
            price,
            count, // 기본값 설정
          },
        ];
      }

      return [
        {
          date: dayjs(value).valueOf(),
          yearMonth,
          price,
          count, // 기본값 설정
          woodaebbangMonthlyAveragePrice: woodaebbangAveragePrice,
        },
      ];
    });

    setChartRealTradeLineData(chartData);

    const validDanjiPriceData = chartData.filter(
      (item) => item.woodaebbangPrice,
    );
    const latestDanjiPriceValue =
      validDanjiPriceData[validDanjiPriceData.length - 1]?.woodaebbangPrice;
    const formattedLatestDanjiPrice = latestDanjiPriceValue
      ? formatPriceToKorean(latestDanjiPriceValue)
      : '-';
    setLatestDanjiPrice(formattedLatestDanjiPrice);

    setChartScatterData(scatterData);

    // chartData 배열의 date 값을 기준으로 tick을 계산하는 함수
    const getXAxisTicks = (): number[] => {
      const desiredTickCount = 4;
      const dataLength = chartData.length;

      // 데이터가 desiredTickCount보다 작으면 모든 date 값을 반환
      if (dataLength <= desiredTickCount) {
        return chartData.map((item) => item.date);
      }

      // 배열의 타입을 명시적으로 number[]로 지정합니다.
      const ticks: number[] = [];
      const step = (dataLength - 1) / (desiredTickCount - 1);

      for (let i = 0; i < desiredTickCount; i += 1) {
        const index = Math.round(i * step);
        ticks.push(chartData[index].date);
      }

      return ticks;
    };

    setQuoteChartOption((prev) => ({
      ...prev,
      xAxisProps: {
        ticks: getXAxisTicks(),
      },
    }));

    await new Promise((resolve) => setTimeout(resolve, 500)); // 2초 동안 로딩
    setChartLoading(false); // 로딩 상태 갱신
  };

  const fetchingComplexPyeongList = async ({
    complexId: complexIdParams,
  }: {
    complexId: number;
  }) => {
    const responsePyeongList = await fetchComplexPyeongList({
      complexId: complexIdParams,
    });

    if (responsePyeongList.data && responsePyeongList.data.length > 0) {
      setFilter((prev) => ({
        ...prev,
        pyeongList: responsePyeongList.data,
      }));
    }
  };

  const initWebViewMessageListener = async () => {
    if (!isMobile) {
      return;
    }

    if (!window.ReactNativeWebView) {
      return;
    }

    const webviewListener = (event: any) => {
      try {
        const { payload, type } = JSON.parse(event.data);
        switch (type) {
          case 'setComplex':
            setParams((prev) => ({
              ...prev,
              complexId: Number(payload.complexId),
              areaInPyeong: Number(payload.areaInPyeong),
              tradeType: payload.tradeType ?? TRADE_TYPE.TRADE,
            }));
            break;
          case 'setArea':
            setParams((prev) => ({
              ...prev,
              areaInPyeong: Number(payload.areaInPyeong),
            }));
            break;
          case 'dismissChartTooltip':
            setQuoteChartOption((prev) => ({
              ...prev,
              tooltipActive: false,
            }));
            break;
          case 'setTradeType':
            setParams((prev) => ({
              ...prev,
              tradeType: payload.tradeType,
            }));
            break;
          case 'setAppVersion':
            dispatch(setAppVersion(payload.appVersion));
            break;
          case 'setDateFilter':
            setFilter((prev) => ({
              ...prev,
              dateFilter: payload.dateFilter,
            }));
            break;
          default:
            break;
        }
      } catch (error) {
        console.log('json pares error', error);
      }
    };

    document.addEventListener('message', webviewListener);
    window.addEventListener('message', webviewListener);
  };

  useEffect(() => {
    initWebViewMessageListener();
  }, []);

  useEffect(() => {
    if (complexId) {
      fetchingComplexPyeongList({ complexId });

      if (areaInPyeong) {
        const urlParams: PriceChartParams = {
          complexId: Number(urlComplexId) || undefined,
          tradeType,
          areaInPyeong,
          startDate,
          endDate,
        };

        const resultParams = urlComplexId ? urlParams : params;
        fetchData(resultParams);
      }
    }

    const visible = isVisibleRealTradeTypeChart
      ? 'isVisibleRealTradeTypeChart'
      : '';
    setChartKey(`${complexId}-${areaInPyeong}-${tradeType}-${visible}`);
  }, [params]);

  useEffect(() => {
    if (urlComplexId) {
      setParams((prev) => ({
        ...prev,
        complexId: Number(urlComplexId),
      }));
    }
  }, [urlComplexId]);

  useEffect(() => {
    if (urlAreaInPyeong) {
      setParams((prev) => ({
        ...prev,
        areaInPyeong: Number(urlAreaInPyeong),
      }));
    }
  }, [urlAreaInPyeong]);

  useEffect(() => {
    if (urlTradeType) {
      if (urlTradeType === 'TRADE' || urlTradeType === 'RENTAL') {
        setParams((prev) => ({
          ...prev,
          tradeType: urlTradeType as TradeType,
        }));
      }
    }
  }, [urlTradeType]);

  useEffect(() => {
    if (pyeongList.length > 0 && params.areaInPyeong === undefined) {
      setParams((prev) => ({
        ...prev,
        areaInPyeong: pyeongList[0],
      }));
    }
  }, [pyeongList]);

  return (
    <Container>
      <Stack>
        <Stack gap="13px">
          <DanjiPriceFilter direction="row" justifyContent="space-between">
            <ChartToggleButtonGroup
              value={tradeType ?? 'TRADE'}
              onChange={handleTradeType}
              buttons={[
                {
                  value: 'TRADE',
                  label: '매매',
                },
                {
                  value: 'RENTAL',
                  label: '전세',
                },
              ]}
            />

            <Stack direction="row" gap={1}>
              {isLatestFilterAppVersion ? (
                <ChartSelect value={dateFilter} onChange={handleDateFilter}>
                  {QUOTE_CHART_DATE_SELECT_FILTER.map(({ value, label }) => (
                    <CustomMenuItem value={value} key={value}>
                      {label}
                    </CustomMenuItem>
                  ))}
                </ChartSelect>
              ) : (
                <ChartDateToggleButtonGroup
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  buttons={QUOTE_CHART_DATE_BUTTONS_FILTER}
                />
              )}

              {isLatestFilterAppVersion ? (
                <ChartSelect
                  value={String(areaInPyeong) || '0'}
                  onChange={handlePyeongFilter}
                >
                  {pyeongList.map((value) => (
                    <CustomMenuItem value={value} key={value}>
                      {value}평
                    </CustomMenuItem>
                  ))}
                </ChartSelect>
              ) : (
                <Button
                  variant="contained"
                  endIcon={<ArrowDownIcon />}
                  onClick={handleClickPyeongFilter}
                  sx={{
                    '&.MuiButton-root': {
                      height: '33px',
                      fontSize: '12px',
                      border: 'none',
                      color: '#777777',
                      background: '#F5F5F5',
                      fontWeight: 700,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {areaInPyeong}평
                </Button>
              )}
            </Stack>
          </DanjiPriceFilter>

          <Wrapper isLatestFilterAppVersion={isLatestFilterAppVersion}>
            {chartLoading ? (
              <CircularProgress sx={{ color: '#9E9E9E' }} />
            ) : (
              <>
                {isChartEmpty ? (
                  <EmptyData
                    minHeight={isLatestFilterAppVersion ? '240px' : '160px'}
                    text="해당 평형에 대한 데이터가 없습니다."
                  />
                ) : (
                  <Stack width="100%">
                    {isLatestFilterAppVersion ? (
                      <Stack direction="row" width="100%" height={200}>
                        <Box
                          sx={{
                            flex: 4,
                            display: 'flex',
                            alignItems: 'stretch',
                          }}
                        >
                          <QuoteChart
                            lineData={chartRealTradeLineData}
                            scatterData={chartScatterData}
                            chartKey={chartKey}
                            chartOption={quoteChartOption}
                            isVisibleDay={isVisibleDay}
                            isVisibleRealTradeTypeChart={
                              isVisibleRealTradeTypeChart
                            }
                            onChartClick={() => showQuoteChartTooltip()}
                            onChartMouseLeave={() => hideQuoteChartTooltip()}
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            position: 'relative',
                            alignItems: 'stretch',
                          }}
                        >
                          <NaverOfferCountChart
                            chartData={naverOfferCountChartData}
                            chartOption={naverOfferCountChartOption}
                            chartKey={chartKey}
                            onChartClick={handleClickNaverOfferChart}
                            onChartMouseLeave={() => {}}
                            height={300}
                          />
                          <TotalNaverOfferCount>
                            {totalNaverOfferCount}개
                          </TotalNaverOfferCount>
                        </Box>
                      </Stack>
                    ) : (
                      <Stack direction="row" width="100%" height={120}>
                        <QuoteChart
                          lineData={chartRealTradeLineData}
                          scatterData={chartScatterData}
                          chartKey={chartKey}
                          chartOption={quoteChartOption}
                          isVisibleDay={isVisibleDay}
                          isVisibleRealTradeTypeChart={
                            isVisibleRealTradeTypeChart
                          }
                          onChartClick={() => showQuoteChartTooltip()}
                          onChartMouseLeave={() => hideQuoteChartTooltip()}
                        />
                      </Stack>
                    )}

                    <DanjiPriceBox>
                      <Box component="span">최신 단지 표준가격</Box>
                      <DanjiPrice>{latestDanjiPrice}</DanjiPrice>
                    </DanjiPriceBox>
                  </Stack>
                )}
              </>
            )}
          </Wrapper>
        </Stack>

        <Stack gap="10px">
          <ChartTypeDashBoard direction="row" gap={1}>
            <Button
              variant="outlined"
              fullWidth
              disableRipple
              endIcon={
                <ButtonTooltip
                  open={guideTooltipOpen}
                  onClose={closeGuideTooltip}
                  title={
                    <>
                      <Box>
                        당일 &apos;호가&apos; 평균*50%
                        <br />
                        당일 기준 30일 이내 &apos;실거래가&apos; 평균 *30%
                        <br />
                        당일 기준 60일 전~30일 전 &apos;실거래가&apos; 평균*15%
                        <br />
                        당일 기준 90일 전~60일 전 &apos;실거래가&apos; 평균*5%
                      </Box>

                      <CloseIcon
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                        }}
                        onClick={closeGuideTooltip}
                      />
                    </>
                  }
                >
                  <IconButton
                    sx={{ zIndex: 3, p: 0 }}
                    onClick={openGuideTooltip}
                  >
                    <InfoIcon />
                  </IconButton>
                </ButtonTooltip>
              }
              sx={{
                '&.MuiButton-root': {
                  color: '#4584FF',
                  border: '1px solid #4584FF',
                  background: 'rgba(69, 132, 255, 0.10)',
                  fontWeight: 700,
                  fontSize: '12px',
                },
                '& .MuiIconButton-root': {
                  zIndex: 0,
                },
                '& .MuiButton-endIcon': {
                  marginLeft: '4px',
                },
              }}
            >
              단지표준가격
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={toggleVisibleRealTradeTypeChart}
              disabled={chartLoading || isRealTradePriceByDateEmpty}
              sx={{
                '&.MuiButton-root': {
                  background: isVisibleRealTradeTypeChart
                    ? 'rgba(69, 132, 255, 0.10)'
                    : '#F5F5F5',
                  color: isVisibleRealTradeTypeChart ? '#4584FF' : '#9E9E9E',
                  fontWeight: 700,
                  fontSize: '12px',
                  border: isVisibleRealTradeTypeChart
                    ? '1px solid #4584FF'
                    : 'none',
                },
              }}
            >
              {isRealTradePriceByDateEmpty
                ? '기간 내 실거래가 없음'
                : '실거래가'}
            </Button>
          </ChartTypeDashBoard>
        </Stack>
      </Stack>

      <WdbModal
        open={ownerCertificationModalOpen}
        onClose={closeOwnerCertificationModal}
      >
        <OwnerCertificationChartPage data={naverOfferCountChartData} />
      </WdbModal>
    </Container>
  );
}

export default QuoteChartPage;

const Container = styled(Stack)`
  user-select: none;
  padding: 10px 20px;
  position: relative;
`;

const Wrapper = styled.div<{ isLatestFilterAppVersion: boolean }>`
  width: 100%;
  min-height: ${({ isLatestFilterAppVersion }) =>
    isLatestFilterAppVersion ? '240px' : '160px'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const DanjiPriceFilter = styled(Stack)``;

const ChartTypeDashBoard = styled(Stack)`
  width: 100%;
`;

const DanjiPriceBox = styled(Box)`
  width: 100%;
  border-radius: 4px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 0;
  color: #9e9e9e;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.28px;
  margin-bottom: 10px;
  gap: 12px;
`;

const DanjiPrice = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: -0.28px;
`;

const TotalNaverOfferCount = styled.div`
  position: absolute;
  bottom: 20px;
  left: 47%;
  transform: translate(-50%, 47%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 4px;
  background: #4584ff;
  padding: 4px 6px;
  width: max-content;
`;

const CustomMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    font-size: 12px;
    padding-top: 0;
    padding-bottom: 0;
    color: #777777;
    font-weight: 700;
    &.Mui-selected {
      color: #fff;
      background: #333;
      &:hover {
        background: #333;
      }
      &:focus-visible {
        background: #333;
      }
    }
  }
`;
